import React, { useState, useEffect, useMemo } from 'react';
import portfolioData from './mini/data.json';

const Work = () => {
  const [activeCategory, setActiveCategory] = useState('AI');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Memoized data transformation to prevent infinite re-renders
  const projects = useMemo(() => {
    const worksData = portfolioData.find(item => item.name === 'works')?.data || [];

    const categoryMap = {
      'W3': 'Blockchain',
      'FSWD': 'Full Stack',
      'PBW': 'Web Apps',
      'GAI': 'Integration',
      'AB': 'Automation',
      'WD': 'Design',
      'PSW': 'Backend',
      'AI': 'AI'
    };

    const getProjectTitle = (src) => {
      const name = src.replace(/\.(png|jpg|jpeg)$/i, '');
      return name.split(/[-_]/).map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    const getTechStack = (categoryCode) => {
      const techMap = {
        'W3': ['React', 'Solidity', 'Web3.js', 'MetaMask'],
        'FSWD': ['React', 'Node.js', 'MongoDB', 'Express'],
        'PBW': ['JavaScript', 'HTML5', 'CSS3', 'API'],
        'GAI': ['Google APIs', 'JavaScript', 'OAuth'],
        'AB': ['Python', 'Selenium', 'API Integration'],
        'WD': ['Figma', 'HTML5', 'CSS3', 'JavaScript'],
        'PSW': ['Python', 'Django', 'PostgreSQL'],
        'AI': ['AI', 'Machine Learning', 'Next.js', 'Node.js']
      };
      return techMap[categoryCode] || ['HTML', 'CSS', 'JavaScript'];
    };

    const getProjectDescription = (title, category) => {
      const descriptions = {
        'Blockchain': 'Decentralized application with smart contract integration',
        'Full Stack': 'Complete web application with frontend and backend',
        'Web Apps': 'Interactive web application with modern UI/UX',
        'Integration': 'API integration and third-party service connection',
        'Automation': 'Automated workflow and process optimization',
        'Design': 'Custom web design and user interface development',
        'Backend': 'Server-side application and database management',
        'AI': 'AI-powered application with intelligent features'
      };
      return descriptions[category] || 'Custom web development project';
    };

    // Remove duplicates based on normalized image path and map to project objects
    const uniqueProjects = [];
    const seenNormalizedImages = new Set();

    for (const work of worksData) {
      const imagePath = typeof work.src === 'string' && work.src.startsWith('websites/')
        ? `/${work.src}`
        : `/websites/${work.src}`;

      // Use the final normalized image path as the de-duplication key
      const normalizedKey = imagePath.toLowerCase();

      if (!seenNormalizedImages.has(normalizedKey)) {
        seenNormalizedImages.add(normalizedKey);

        const isVideoLink = work.isVideo || !!(work.link && (work.link.toLowerCase().includes('.mp4') || work.link.includes('drive.google.com')));
        const videoLink = isVideoLink ? work.link : (work.video || null);
        const liveLink = work.live || (!isVideoLink && work.link ? work.link : null);

        uniqueProjects.push({
          id: work.id,
          title: work.title || getProjectTitle(work.src),
          category: categoryMap[work.code] || 'Web Apps',
          image: imagePath,
          link: work.link,
          isLive: !!liveLink,
          categoryCode: work.code,
          techStack: Array.isArray(work.tags) && work.tags.length > 0 ? work.tags : getTechStack(work.code),
          description: getProjectDescription(work.title || getProjectTitle(work.src), categoryMap[work.code] || 'Web Apps'),
          isVideo: !!videoLink,
          videoLink,
          liveLink
        });
      }
    }

    // Sort by optional priority (desc), fallback to original order
    uniqueProjects.sort((a, b) => {
      const ap = (worksData.find(w => w.id === a.id)?.priority) || 0;
      const bp = (worksData.find(w => w.id === b.id)?.priority) || 0;
      return bp - ap;
    });

    // Return first 18 unique projects for the "Selected Work" section
    return uniqueProjects.slice(0, 18);
  }, []); // Empty dependency array since portfolioData is imported and static

  // Enhanced categories with icons and counts
  const categories = useMemo(() => {
    const cats = [
      { id: 'all', name: 'All Projects', icon: 'fas fa-th-large' },
      { id: 'Blockchain', name: 'Blockchain', icon: 'fab fa-bitcoin' },
      { id: 'Full Stack', name: 'Full Stack', icon: 'fas fa-layer-group' },
      { id: 'AI', name: 'AI', icon: 'fas fa-brain' },
      { id: 'Automation', name: 'Automation', icon: 'fas fa-robot' },
      { id: 'Design', name: 'Design', icon: 'fas fa-palette' }
    ];

    return cats.map(cat => ({
      ...cat,
      count: cat.id === 'all' ? projects.length : projects.filter(p => p.category === cat.name).length
    }));
  }, [projects]);

  const handleCategoryChange = async (categoryId) => {
    setIsLoading(true);
    setActiveCategory(categoryId);

    // Simulate loading for smooth UX
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };



  // Enhanced filtering with search
  useEffect(() => {
    const filtered = activeCategory === 'all'
      ? projects
      : projects.filter(project => project.category === activeCategory);

    setFilteredProjects(filtered);
  }, [activeCategory, projects]);

  const openProjectLink = (url) => {
    if (!url || url === '') return;

    let targetUrl = url;
    if (url.startsWith('websites/')) {
      targetUrl = `/${url}`;
    }

    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  // Loading skeleton component
  const ProjectSkeleton = () => (
    <div className="project-card-simple skeleton">
      <div className="project-image-simple skeleton-image"></div>
      <div className="project-content-simple">
        <div className="skeleton-title"></div>
        <div className="skeleton-category"></div>
        <div className="skeleton-tech"></div>
      </div>
    </div>
  );

  return (
    <section id="work" className="work-section-simple">
      <div className="container">
        {/* Enhanced Header */}
        <div className="work-header-simple">
          <h2 className="work-title-simple animate__animated animate__fadeInUp">
            Selected Work
          </h2>
          <p className="work-subtitle-simple animate__animated animate__fadeInUp animate__delay-1s">
            A showcase of our recent projects and digital solutions
          </p>
        </div>

        {/* Search Bar */}


        {/* Enhanced Category Filter */}
        <div className="work-filter-simple">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-btn-simple ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
            >
              <i className={category.icon}></i>
              <span>{category.name}</span>
              <span className="category-count">{category.count}</span>
            </button>
          ))}
        </div>

        {/* Results Count */}


        {/* Enhanced Projects Grid */}
        <div className="projects-grid-simple">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, index) => (
              <ProjectSkeleton key={index} />
            ))
          ) : filteredProjects.length > 0 ? (
            filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="project-card-simple animate__animated animate__fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
                title={project.description}
              >
                <div className="project-image-simple">
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/assets/placeholder.png';
                    }}
                  />

                  {project.isLive && (
                    <div className="live-indicator">
                      <i className="fas fa-globe"></i>
                      Live
                    </div>
                  )}
                  <div className="project-overlay">
                    <div className="overlay-content">
                      <div
                        className="overlay-actions"
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '10px',
                          flexWrap: 'wrap',
                          textAlign: 'center'
                        }}
                      >
                        {project.isVideo && (
                          <button
                            className="btn-watch-video"
                            onClick={(e) => {
                              e.stopPropagation();
                              openProjectLink(project.videoLink || project.link);
                            }}
                          >
                            <i className="fas fa-play"></i> Watch Video
                          </button>
                        )}
                        {project.isLive && (
                          <button
                            className="btn-visit-site"
                            onClick={(e) => {
                              e.stopPropagation();
                              openProjectLink(project.liveLink || project.link);
                            }}
                          >
                            <i className="fas fa-external-link-alt"></i> Visit Site
                          </button>
                        )}
                        {!project.isVideo && !project.isLive && (
                          <button
                            className="btn-open-image"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(project.image, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            <i className="fas fa-image"></i> Open Image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="project-content-simple">
                  <h3 className="project-title-simple">{project.title}</h3>
                  <p className="project-category-simple">{project.category}</p>
                  <div className="tech-stack">
                    {project.techStack.slice(0, 3).map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                    {project.techStack.length > 3 && (
                      <span className="tech-more">+{project.techStack.length - 3}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              <h3>No projects found</h3>
              <p>Try adjusting your category filter.</p>
            </div>
          )}
        </div>

        {/* Call-to-Action Section */}
        <div className="work-cta-section">
          <div className="cta-inner">
            <h3>Have a Project in Mind?</h3>
            <p>Let's discuss how we can help bring your ideas to life</p>
            <div className="cta-actions">
              <a href="#contact" className="cta-button primary">
                <i className="fas fa-rocket"></i>
                Get Started
              </a>
              <a href="#services" className="cta-button secondary">
                <i className="fas fa-eye"></i>
                View Services
              </a>
            </div>
          </div>
        </div>
      </div>


    </section>
  );
};

export default Work;